import { useState } from "react";
import { Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { firstAvailable, img } from "@/lib/api";
import { AddToBagButton } from "@/components/Cta";
import { Emblem } from "@/components/Logo";
import { Availability, Price, VariantChips, WishButton } from "@/components/ProductBits";

const Media = ({ p, index, className = "" }) => (
  <div className={`group/media relative overflow-hidden rounded-[22px] bg-blush w-fit max-w-full ${className}`}>
    <Link to={`/product/${p.slug}`} data-testid={`product-image-link-${p.slug}`} className="block relative">
      {p.images[0] ? (
        <img src={img(p.images[0])} alt={p.name} loading="lazy" className="fit-view transition-transform duration-[1400ms] ease-out group-hover/media:scale-[1.04]" />
      ) : (
        <span className="aspect-[4/5] grid place-items-center text-center text-plum/50"><span><Emblem className="w-14 h-12 mx-auto" /><span className="block mt-3 text-[13px] font-bold">Photo coming soon</span></span></span>
      )}
      {p.images[1] && (
        <img src={img(p.images[1])} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-contain bg-blush opacity-0 transition-opacity duration-700 group-hover/media:opacity-100" />
      )}
    </Link>
    <span className="facet absolute left-4 top-4 rounded-[10px] bg-pearl/95 px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-plum pointer-events-none">
      {String(index + 1).padStart(2, "0")} · {p.label || p.type}
    </span>
    <WishButton p={p} className="absolute right-4 top-4" />
  </div>
);

const Actions = ({ p, variant, light, size }) => {
  const { addToBag } = useStore();
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      <AddToBagButton
        testId={`add-to-bag-${p.slug}`}
        variant={light ? "pearl" : "plum"}
        size={size}
        disabled={p.available <= 0}
        onAdd={() => addToBag(p, variant, 1)}
      />
      <Link to={`/product/${p.slug}`} data-testid={`view-details-${p.slug}`} className={`rh-link ${light ? "text-pearl" : "text-plum"}`}>
        View details
      </Link>
    </div>
  );
};

export const ProductCard = ({ p, index = 0, layout = "standard" }) => {
  const [variant, setVariant] = useState(firstAvailable(p));

  if (layout === "band") {
    return (
      <article data-testid={`product-card-${p.slug}`} className="grid lg:grid-cols-12 gap-3 items-center rounded-[28px] bg-plum p-3 text-pearl">
        <Media p={p} index={index} className="lg:col-span-6" />
        <div className="lg:col-span-6 flex flex-col justify-center gap-6 p-5 sm:p-8 lg:p-12 relative">
          <span className="font-display italic text-champagne text-[64px] lg:text-[96px] leading-none absolute right-6 top-2 lg:top-6 opacity-90 select-none">
            {String(index + 1).padStart(2, "0")}
          </span>
          <p className="eyebrow text-champagne">{p.type}</p>
          <Link to={`/product/${p.slug}`}>
            <h3 className="font-display text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.02] font-medium hover:text-champagne transition-colors">{p.name}</h3>
          </Link>
          <p className="text-[16px] lg:text-[17px] leading-relaxed text-pearl/80 max-w-md">{p.tagline}</p>
          <Price p={p} light />
          <Availability p={p} light />
          <VariantChips p={p} value={variant} onChange={setVariant} light />
          <Actions p={p} variant={variant} light />
        </div>
      </article>
    );
  }

  const feature = layout === "feature";
  return (
    <article data-testid={`product-card-${p.slug}`} className="group">
      <Media p={p} index={index} />
      <div className="pt-6 lg:pt-7 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="eyebrow text-berry mb-2">{p.type}</p>
            <Link to={`/product/${p.slug}`}>
              <h3 className={`font-display font-medium leading-[1.05] hover:text-berry transition-colors ${feature ? "text-[32px] sm:text-[40px]" : "text-[30px] sm:text-[34px]"}`}>
                {p.name}
              </h3>
            </Link>
          </div>
          <Price p={p} />
        </div>
        <p className="text-[16px] leading-relaxed text-plum/75 max-w-lg">{p.tagline}</p>
        <Availability p={p} />
        <VariantChips p={p} value={variant} onChange={setVariant} />
        <Actions p={p} variant={variant} />
      </div>
    </article>
  );
};
